import React, { useCallback, useMemo } from 'react';
import type { AnalysisProps } from '../../types';
import { useRecoilValue } from 'recoil';
import { blockDisplayHeader, blockFonts, getCustomFontSize, studioPrintOrientationType } from 'venn-state';
import { pairwiseCorrelationExtractor } from '../../../studio-blocks/components/charts/pairwise-correlation/dataExtractors';
import { type ExcelCell, ptToPx } from 'venn-utils';
import useExportUpdate from '../../../studio-blocks/logic/useExportUpdate';
import type { TabularDataRow } from '../../../studio-blocks/components/charts/pairwise-correlation/types';
import { EllipsisTooltipSpan } from 'venn-ui-kit';
import CorrelationColorLegend from '../../../studio-blocks/components/charts/pairwise-correlation/CorrelationColorLegend';
import {
  CorrelationColumnHeaderCell,
  CorrelationLabelHeaderCell,
  CorrelationValueCell,
  DATA_COLUMN_MIN_WIDTH_PX,
  HEADER_HEIGHT_PX,
  LABEL_CELL_TOP_PADDING_PX,
  LABEL_COLUMN_WIDTH_PX,
  REPORT_LAB_DATA_COLUMN_MIN_WIDTH_PX,
  ROW_HEIGHT_FONT_MULTIPLIER,
} from '../../../studio-blocks/components/charts/pairwise-correlation/agGridColumns';
import type { Analysis } from 'venn-api';
import { FIRST_COLUMN_INNER_WIDTH } from '../../components/visualizations/grids/correlation-grid';
import { useBlockSubjectSuperscripts } from '../../components/core/block-subject-footnotes-and-superscripts';
import { useIsCorrelationLabelFull } from '../../../studio-blocks/logic/useCorrelationLabelDisplay';
import { useBlockContext } from '../../contexts/block-context';
import { GridWrapper } from '../../../studio-blocks/common';
import { useCommonGridProps } from '../../../studio-blocks/logic/columnUtils';
import ExportableGrid from '../../../studio-blocks/components/grid/ExportableGrid';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import type { StudioRequestSubject } from 'venn-state';

type PairwiseCorrelationProps = Omit<AnalysisProps, 'analyses'> & {
  analysis: Analysis | undefined;
};

const CorrelationLabel = ({
  label,
  subject,
  isBenchmark,
  isFullLabelDisplay,
}: {
  label: string;
  subject: StudioRequestSubject;
  isBenchmark: boolean;
  isFullLabelDisplay: boolean;
}) => {
  const superscripts = useBlockSubjectSuperscripts({
    subject,
    isBenchmark,
  });
  const labelContent = [<sup>{superscripts}</sup>, label];

  return isFullLabelDisplay ? (
    <span>{labelContent}</span>
  ) : (
    <span>
      <EllipsisTooltipSpan usePortal maxWidth={FIRST_COLUMN_INNER_WIDTH}>
        {labelContent}
      </EllipsisTooltipSpan>
    </span>
  );
};

export const PairwiseCorrelation = ({
  id,
  requests,
  analysis,
  exportMetaData,
  inPrintMode,
  isReport,
}: PairwiseCorrelationProps) => {
  const tableFont = useRecoilValue(blockFonts.blockTableData(id));
  const legendFont = useRecoilValue(blockFonts.blockChartLegend(id));
  const printOrientation = useRecoilValue(studioPrintOrientationType);
  const header = useRecoilValue(blockDisplayHeader(id));
  const isFullLabelDisplay = useIsCorrelationLabelFull();
  const { setTrailerElement } = useBlockContext();
  const subjects = useMemo(() => requests.map((req) => req.subject), [requests]);

  const data = useMemo(
    () => pairwiseCorrelationExtractor(subjects, requests, analysis),
    [subjects, requests, analysis],
  );

  const excelDataFn = useCallback(() => {
    const isRelative = requests[0]?.relative;
    const benchmarks = requests.map((r) => r.benchmark);
    const headerRow: ExcelCell[] = data.map((data, index) => ({
      value: isRelative ? `${data.label} (Relative to ${benchmarks[index]?.name ?? '--'})` : data.label,
      bold: true,
    }));
    const body: ExcelCell[][] = data.map((row) => {
      const cellData: ExcelCell[] = [];
      for (let subjectIdx = 0; subjectIdx < requests.length; subjectIdx++) {
        cellData.push({
          value: row[subjectIdx]?.value ?? '--',
          percentage: row?.type === 'percentage',
          digits: 2,
        });
      }
      return [{ value: row.label, bold: true }, ...cellData];
    });
    return [[{ value: header, bold: true }, ...headerRow], ...body];
  }, [data, requests, header]);

  useExportUpdate({ selectedRefId: id, exportMetaData, excelDataFn });

  const correlationLabelCellRenderer = useCallback(
    (params: ICellRendererParams<TabularDataRow>) => {
      const rowData = params.data;
      if (!rowData) return null;
      return (
        <CorrelationLabel
          label={rowData.label}
          subject={rowData.subject}
          isBenchmark={rowData.isBenchmark}
          isFullLabelDisplay={isFullLabelDisplay}
        />
      );
    },
    [isFullLabelDisplay],
  );

  const rowHeightPx = ptToPx(tableFont.fontSizePt) * ROW_HEIGHT_FONT_MULTIPLIER[printOrientation];
  const labelCellTopPaddingPx = LABEL_CELL_TOP_PADDING_PX[printOrientation];
  const dataColumnMinWidthPx = isReport ? REPORT_LAB_DATA_COLUMN_MIN_WIDTH_PX : DATA_COLUMN_MIN_WIDTH_PX;
  const cellFontSize = getCustomFontSize(tableFont);

  const extraDefaultColDef = useMemo<ColDef>(
    () => ({
      autoHeaderHeight: false,
      wrapHeaderText: false,
      cellStyle: { fontSize: cellFontSize },
    }),
    [cellFontSize],
  );
  const commonGridProps = useCommonGridProps(extraDefaultColDef);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        colId: 'label',
        width: LABEL_COLUMN_WIDTH_PX,
        suppressSizeToFit: true,
        sortable: false,
        resizable: false,
        headerComponent: CorrelationLabelHeaderCell,
        headerComponentParams: { fontSize: cellFontSize },
        cellStyle: { paddingLeft: 5, paddingTop: labelCellTopPaddingPx, fontSize: cellFontSize },
        cellRenderer: correlationLabelCellRenderer,
      },
      ...data.map(
        (col, colNum): ColDef => ({
          colId: `col-${colNum}`,
          minWidth: dataColumnMinWidthPx,
          sortable: false,
          resizable: false,
          headerComponent: CorrelationColumnHeaderCell,
          headerComponentParams: { columnLabel: col.label },
          cellStyle: { padding: 0 },
          cellRenderer: CorrelationValueCell,
          cellRendererParams: { colNum },
        }),
      ),
    ],
    [data, correlationLabelCellRenderer, cellFontSize, labelCellTopPaddingPx, dataColumnMinWidthPx],
  );

  return (
    <>
      <GridWrapper isReport={isReport} className="[&_.ag-header]:border-b-venn-black">
        <ExportableGrid
          {...commonGridProps}
          selectedRefId={id}
          exportable={false}
          inPrintMode={inPrintMode}
          isCompact={false}
          rowData={data}
          columnDefs={columnDefs}
          rowHeight={rowHeightPx}
          headerHeight={HEADER_HEIGHT_PX}
          domLayout={'autoHeight' as const}
          suppressRowVirtualisation
          suppressRowTransform
          scrollbarWidth={10}
        />
      </GridWrapper>
      <div ref={setTrailerElement} className="flow-root">
        <CorrelationColorLegend fontSize={getCustomFontSize(legendFont)} />
      </div>
    </>
  );
};
